/**
 * Reporting stage orchestrator. See atlas.ts / volumes.ts / render.ts /
 * interpretation.ts for the individual pieces and their scope notes.
 *
 * PRECONDITION (documented, not enforced beyond a dims check in atlas.ts):
 * `lesionProb`/`brainMask` must already be registered to the MNI182 grid
 * this app's bundled atlases use (182x218x182) — either by the real
 * Registration stage (when enabled) or a pre-registered demo fixture.
 */
import { computeAllAtlasOverlaps, computeExtraAtlasOverlaps, computeVentricleFeatures, type RegionStat, type VentricleFeatures } from "./atlas";
import { computeLesionVolumesMl, estimateIcvMl } from "./volumes";
import { renderReportHtml, type ReportData } from "./render";
import { computeInterpretation, computeHydrocephalus, type HydroCall } from "./interpretation";
import { buildAllQfvCsvs, buildAspectsPcQfv, buildBpmType1Qfv, buildVentriclesQfv, qfvVolml, ventriclesQfvValues } from "./qfv";
import { buildTextReport } from "./textReport";
import type { Dims } from "../volume";

export interface GenerateReportInput {
  subjectId: string;
  /** Lesion probability/mask on the MNI182 grid, F-order, same convention as every other worker in this app. */
  lesionProb: Float32Array;
  dims: Dims;
  pixdim: Dims;
  /** Optional brain mask (also MNI182 grid) for ICV; falls back to the Python source's 1200mL default if omitted. */
  brainMask?: Uint8Array;
  /** Whether this run's Lesion Seg input came from the real Registration stage vs. a pre-registered demo fixture. */
  usingRealRegistration?: boolean;
  /**
   * Optional MNI-space ADC on the same grid as `lesionProb` (any scale — it
   * is z-normalised inside `brainMask`). When given together with
   * `brainMask`, the VENTRICLES QFV/lesionload (native `get_FV_prob`'s
   * `Ventricles_LVO`/`Ventricles_LVI`) are computed too.
   */
  adcMni?: ArrayLike<number>;
}

export interface GenerateReportResult {
  data: ReportData;
  html: string;
}

export async function generateReport(input: GenerateReportInput): Promise<GenerateReportResult> {
  const volumes = computeLesionVolumesMl(input.lesionProb, input.dims, input.pixdim);
  const icvMl = estimateIcvMl(input.brainMask ?? null, input.dims, input.pixdim);
  const atlasStats: Record<string, RegionStat[]> = await computeAllAtlasOverlaps(volumes.binaryMask, input.dims);
  // ASPECTSPC / BPM_TYPE1 (+ VENTRICLES): additive outputs only, so a failure
  // here must not take down the report the 3 core atlases already produced.
  try {
    Object.assign(atlasStats, await computeExtraAtlasOverlaps(volumes.binaryMask, input.dims));
  } catch (err) {
    console.warn(`[report] ASPECTSPC/BPM_TYPE1 atlas overlap skipped: ${(err as Error).message}`);
  }
  let ventricles: VentricleFeatures | undefined;
  if (input.adcMni && input.brainMask) {
    try {
      ventricles = await computeVentricleFeatures(input.adcMni, input.brainMask, input.dims);
    } catch (err) {
      console.warn(`[report] VENTRICLES features skipped: ${(err as Error).message}`);
    }
  }
  let hydrocephalus: HydroCall | undefined;
  if (ventricles) {
    try {
      hydrocephalus = await computeHydrocephalus(qfvVolml(volumes.totalMl), ventriclesQfvValues(ventricles));
    } catch (err) {
      console.warn(`[report] hydrocephalus model skipped: ${(err as Error).message}`);
    }
  }
  const interpretation = await computeInterpretation(atlasStats, volumes.totalMl, input.pixdim);

  const data: ReportData = {
    subjectId: input.subjectId,
    volumes,
    icvMl,
    atlasStats,
    interpretation,
    usingRealRegistration: input.usingRealRegistration,
    ventricles,
    hydrocephalus,
  };
  return { data, html: renderReportHtml(data) };
}

export interface ReportFileSet {
  /** Keyed by real native file stem (DWI: `VASCULAR_QFV` etc; PWI: `Vascular_HPQFV` etc — caller decides the stem convention). */
  vascularQfvCsv: string;
  vascularLesionloadCsv: string;
  lobeQfvCsv: string;
  lobeLesionloadCsv: string;
  aspectsQfvCsv: string;
  aspectsLesionloadCsv: string;
  /** Native stem `ASPECTSPC` (both modalities); absent if that atlas failed to load. */
  aspectsPcQfvCsv?: string;
  aspectsPcLesionloadCsv?: string;
  /** Native stem `BPM_TYPE1` (both modalities); absent if that atlas failed to load. */
  bpmType1QfvCsv?: string;
  bpmType1LesionloadCsv?: string;
  /** Native stem `VENTRICLES`; present only when `generateReport` got `adcMni` + `brainMask`.
   *  The native reference run writes these for DWI only (`VENTRICLES_QFV`/`_lesionload`). */
  ventriclesQfvCsv?: string;
  ventriclesLesionloadCsv?: string;
  textReport: string;
}

/**
 * Per-file CSV/text exports matching the real native pipeline's file set
 * (see `qfv.ts`/`textReport.ts`) — additive to `generateReport()`'s
 * consolidated JSON+HTML, which stays as the UI's own report viewer input.
 * `modality` only affects the text report's wording ("restricted diffusion"
 * vs "hypoperfusion"); the CSV content/column names are atlas-driven, not
 * modality-driven (the real pipeline's own QFV column names are identical
 * between its DWI and PWI CSVs, only the file-stem casing/`HP` prefix and
 * row content differ, which is the caller's responsibility when choosing
 * output paths).
 */
export function buildReportFileSet(data: ReportData, modality: "DWI" | "PWI"): ReportFileSet {
  const qfv = buildAllQfvCsvs(data.subjectId, data.atlasStats, data.volumes.totalMl);
  const textReport = buildTextReport(data.subjectId, modality, data.volumes, data.interpretation, data.hydrocephalus);
  const aspectsPc = data.atlasStats.aspectpc ? buildAspectsPcQfv(data.subjectId, data.atlasStats.aspectpc, data.volumes.totalMl) : undefined;
  const bpm = data.atlasStats.bpmType1 ? buildBpmType1Qfv(data.subjectId, data.atlasStats.bpmType1, data.volumes.totalMl) : undefined;
  const vent = data.ventricles ? buildVentriclesQfv(data.subjectId, data.ventricles, data.volumes.totalMl) : undefined;
  return {
    vascularQfvCsv: qfv.vascular.qfvCsv,
    vascularLesionloadCsv: qfv.vascular.lesionloadCsv,
    lobeQfvCsv: qfv.lobe.qfvCsv,
    lobeLesionloadCsv: qfv.lobe.lesionloadCsv,
    aspectsQfvCsv: qfv.aspects.qfvCsv,
    aspectsLesionloadCsv: qfv.aspects.lesionloadCsv,
    aspectsPcQfvCsv: aspectsPc?.qfvCsv,
    aspectsPcLesionloadCsv: aspectsPc?.lesionloadCsv,
    bpmType1QfvCsv: bpm?.qfvCsv,
    bpmType1LesionloadCsv: bpm?.lesionloadCsv,
    ventriclesQfvCsv: vent?.qfvCsv,
    ventriclesLesionloadCsv: vent?.lesionloadCsv,
    textReport,
  };
}

export { ATLASES, computeAtlasOverlap, computeAllAtlasOverlaps, categoryFeatures } from "./atlas";
export { EXTRA_ATLASES, VENTRICLES_ATLAS, computeExtraAtlasOverlaps, computeVentricleFeatures } from "./atlas";
export { buildAspectsPcQfv, buildBpmType1Qfv, buildVentriclesQfv, ventriclesQfvValues, qfvVolml } from "./qfv";
export { computeLesionVolumesMl, estimateIcvMl, voxelVolumeMl } from "./volumes";
export { renderReportHtml } from "./render";
export { computeInterpretation, computeHydrocephalus } from "./interpretation";
export type { RegionStat, AtlasDef, AtlasLookup, VentricleFeatures } from "./atlas";
export type { LesionVolumes } from "./volumes";
export type { ReportData } from "./render";
export type { InterpretationResult, RegionCall, HydroCall } from "./interpretation";
